package com.iregained.site.components.sections.testimonials.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.testimonials.styles.TestimonialCardStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.utils.LocalBreakpoint
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text

// UNIQUE ANIMATION: Staggered Glide-Up
@Composable
private fun Modifier.staggeredGlideUp(isVisible: Boolean, delayMs: Int): Modifier {
    return this.opacity(if (isVisible) 1 else 0).translateY(if (isVisible) 0.px else 30.px).transition(
        Transition.of("opacity", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms),
        Transition.of("transform", 0.6.s, TransitionTimingFunction.EaseOut, delayMs.ms)
    )
}

@Composable
fun TestimonialsBenefitsSection(data: TestimonialsBenefitsData = defaultTestimonialsBenefitsData) {
    val breakpoint = LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val palette = ColorMode.current.toSitePalette()
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "clinical-benefits-summary"
    var isVisible by remember { mutableStateOf(false) }

    if (data.benefits.isNotEmpty()) {
        Box(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(Colors.White)
                .padding(topBottom = if (isDesktop) 80.px else 40.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") }, contentAlignment = Alignment.Center
        ) {
            Div(
                attrs = Modifier.fillMaxWidth().display(DisplayStyle.Flex)
                    .justifyContent(org.jetbrains.compose.web.css.JustifyContent.Center).toAttrs {
                        ref { element ->
                            val observer = IntersectionObserver { entries, obs ->
                                entries.forEach {
                                    if (it.isIntersecting) {
                                        isVisible = true; obs.unobserve(element)
                                    }
                                }
                            }
                            observer.observe(element); onDispose { observer.disconnect() }
                        }
                    }) {
                Column(
                    modifier = Modifier.fillMaxWidth().maxWidth(1200.px),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    SectionHeader(
                        text = data.headline,
                        fontSize = 32.px,
                        lineWidthPercent = 25.percent,
                        modifier = Modifier.margin(bottom = 16.px).opacity(if (isVisible) 1 else 0)
                            .transition(Transition.of("opacity", 0.8.s)),
                        alignment = Alignment.CenterHorizontally
                    )

                    Div(
                        attrs = SubheadlineTextStyle.toModifier().maxWidth(900.px).fontSize(18.px)
                            .fontWeight(FontWeight.Medium).color(palette.custom.darkText).textAlign(TextAlign.Center)
                            .margin(bottom = 48.px).opacity(if (isVisible) 1 else 0)
                            .transition(Transition.of("opacity", 0.8.s, delay = 150.ms)).toAttrs {
                                ref { htmlElement ->
                                    htmlElement.innerHTML = data.subtitle; onDispose {}
                                }
                            })

                    SimpleGrid(
                        numColumns = numColumns(base = 1, md = 2), modifier = Modifier.fillMaxWidth().gap(32.px)
                    ) {
                        data.benefits.forEachIndexed { index, benefit ->
                            key(benefit.title) {
                                BenefitCard(benefit, palette, Modifier.staggeredGlideUp(isVisible, 200 + (index * 100)))
                            }
                        }
                    }

                    // Author Attribution block
                    Column(
                        modifier = Modifier.fillMaxWidth().margin(top = 40.px).padding(top = 24.px)
                            .borderTop(1.px, LineStyle.Solid, Color.rgb(0xEEEEEE)).staggeredGlideUp(isVisible, 800),
                        horizontalAlignment = Alignment.CenterHorizontally
                    ) {
                        Div(
                            attrs = Modifier.fontSize(20.px).fontWeight(FontWeight.Medium).fontStyle(FontStyle.Italic)
                                .color(palette.custom.darkText).textAlign(TextAlign.Center).maxWidth(800.px)
                                .margin(bottom = 16.px).toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML =
                                            "“Our experience so far has reinforced that MyHand<sup>®</sup> is a powerful tool in neuro-rehabilitation, particularly in bridging the gap between early movement return and functional hand use.”"
                                        onDispose {}
                                    }
                                })
                        Div(
                            attrs = Modifier.fontSize(16.px).fontWeight(FontWeight.Bold).color(palette.brand.primary)
                                .toAttrs {
                                    ref { htmlElement ->
                                        htmlElement.innerHTML =
                                            "- Mona Kalra, PT, Maplecare Physiotherapy, Ottawa, Canada"
                                        onDispose {}
                                    }
                                })
                    }
                }
            }
        }
    }
}

@Composable
private fun BenefitCard(item: ClinicalBenefitItem, palette: com.iregained.site.theme.SitePalette, modifier: Modifier) {
    Box(modifier = TestimonialCardStyle.toModifier().backgroundColor(Color.rgb(0xF9FAFB)).then(modifier)) {
        Column(modifier = Modifier.fillMaxSize()) {
            Row(modifier = Modifier.margin(bottom = 16.px), verticalAlignment = Alignment.CenterVertically) {
                // Hover scale transition to the number circle
                Box(
                    modifier = Modifier.size(40.px).borderRadius(50.percent).backgroundColor(palette.brand.primarySoft)
                        .color(palette.brand.primary).fontSize(20.px).fontWeight(FontWeight.Bold).margin(right = 16.px)
                        .transition(Transition.of("transform", 0.3.s, TransitionTimingFunction.EaseOut)),
                    contentAlignment = Alignment.Center
                ) {
                    Div { Text(item.number.toString()) }
                }

                Div(
                    attrs = Modifier.fontSize(20.px).fontWeight(FontWeight.Bold).color(palette.custom.darkText)
                        .toAttrs {
                            ref { htmlElement ->
                                htmlElement.innerHTML = item.title
                                onDispose {}
                            }
                        })
            }
            Div(
                attrs = Modifier.fontSize(16.px).lineHeight(1.6).color(Colors.Gray).toAttrs {
                    ref { htmlElement ->
                        htmlElement.innerHTML = item.description
                        onDispose {}
                    }
                })
        }
    }
}

// ==========================================
// DATA MODELS
// ==========================================

data class ClinicalBenefitItem(
    val number: Int, val title: String, val description: String
)

data class TestimonialsBenefitsData(
    val sectionId: String? = null, val headline: String, val subtitle: String, val benefits: List<ClinicalBenefitItem>
)

val defaultTestimonialsBenefitsData = TestimonialsBenefitsData(
    sectionId = "clinical-benefits-summary",
    headline = "SUMMARY OF CLINICAL BENEFITS",
    subtitle = "Observations recorded by physical therapists and clinical directors integrating the MyHand<sup>®</sup> System.",
    benefits = listOf(
        ClinicalBenefitItem(
            number = 1,
            title = "Sensitive Detection of Micro-Movements",
            description = "The device identifies subtle motor activation—often before movement is visible or palpable—allowing for earlier recognition of recovery and significantly enhanced patient engagement compared to traditional hand therapy."
        ), ClinicalBenefitItem(
            number = 2,
            title = "Reduction in Spasticity Post-Treatment",
            description = "Sessions commonly result in decreased tone and improved hand relaxation, which then facilitates more effective participation in functional task training."
        ), ClinicalBenefitItem(
            number = 3,
            title = "Facilitates Finger Isolation & Relaxation",
            description = "Supports cortical reorganization and helps patients learn to activate or relax specific fingers with improved feedback and accuracy."
        ), ClinicalBenefitItem(
            number = 4,
            title = "Objective Measurement & Progress",
            description = "Provides quantifiable metrics that improve clinical decision-making while giving patients clear, motivating feedback on their performance."
        ), ClinicalBenefitItem(
            number = 5,
            title = "Optimizes Functional Integration",
            description = "When paired with expert upper-extremity rehabilitation strategies and task-specific training, MyHand<sup>®</sup> contributes to meaningful and measurable improvements in hand use and daily function."
        )
    )
)